import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import i18n from 'live/services/i18n'
import InfiniteScroll from 'react-infinite-scroll-component'
import { deepFind, formatDecimalNumber } from '../../utils/utils'


export type ColumnProp = {
    name: string
    className?: string
    path?: string
}

export type ActionProp = {
    classNameIcon?: string
    action(ele): void
    tooltipTitle?: string
    children?: JSX.Element
}

type DataProp =  {values: any[], columns: ColumnProp[]} 

type IProps = {   
    data: DataProp,
    total: number
    totalPage: number
    totalDataMessage?: string,
    emptyMessage?: string
    emptyActionTitle?: string
    emptyActionRedirect?: string
    actions?: ActionProp[]
    hideTotal?: boolean
    loadMore?: boolean,
    isLoading?: boolean,
    isPaginating?: boolean,
    next?(total: number): void
    rowClick?(ele): void
}

export const CustomTableV2 = ({   
    data = {values: [], columns: []}, 
    actions = [],
    emptyMessage = '',
    emptyActionTitle = '',
    emptyActionRedirect,
    totalDataMessage = 'results',
    total = 0,
    totalPage = 12,
    hideTotal = false,
    isLoading = false,
    isPaginating = false,
    rowClick,
    next
}: IProps): JSX.Element => {
    const [array, setArray] = useState<any>([[]])
    const [state, setState] = useState<any>({ total, totalPage })    

    useEffect(() => {        
        const {values = [], columns = []} = data

        const arrayAux = []
        columns.forEach(column => {
            arrayAux[column.name] = []

            values.forEach((ele, index) => {
                arrayAux[column.name][index] = ele
            })
        })

        setArray(arrayAux)
    }, [data])

    useEffect(() => {
        setState({ ...state, ...{ total, totalPage } })
    }, [total, totalPage])

    const RenderTD = ({ cell, path, html }: any): JSX.Element => {
        return (
            <StyledTD className="no-padding">
                <div className="relative">
                    <div className="row">
                        <a style={{paddingLeft: 0}} className="col-md-12">
                            <div className="col-md-12 table-name">{deepFind(cell, path)}</div>
                        </a>
                    </div>
                </div>
            </StyledTD>
        )
    }

    const RenderActions = ({ actions, data }: any): JSX.Element => {
        if (actions.length === 0) return <> </>

        return (
            <td className="no-padding">
                <div className="relative">
                    <div className="list-actions">
                        <ul>
                            {actions.map(({ action, classNameIcon, tooltipTitle, children }: ActionProp, index) => {
                                return (
                                    <li key={index}>
                                        <a
                                            role="menuitem"
                                            onClick={e => {
                                                e.stopPropagation()
                                                action(data)
                                            }}
                                        >
                                            {classNameIcon && <i title={tooltipTitle} className={classNameIcon}></i>}
                                            {children}
                                            
                                        </a>
                                    </li>
                                )
                            })}
                        </ul>
                    </div>
                </div>
            </td>
        )
    }

    const RenderTR = ({ data = [], columns = [] }: any): JSX.Element => {
        return (
            <>
                {data.map((ele, index) => {
                    return (
                        <tr key={index} onClick={() => rowClick?.(ele)}>
                            {columns.map((column: ColumnProp, index2) => {
                                let cell

                                if (array[column?.name]) {
                                    cell = array[column?.name][index]
                                }

                                return <RenderTD key={index2} cell={cell} path={column.path} />
                            })}

                            <RenderActions actions={actions} data={ele} />
                        </tr>
                    )
                })}
            </>
        )
    }

    const RenderTH = ({ name, className }: ColumnProp): JSX.Element => {
        return (
            <th className={`${className}`}>
                <a>{i18n(name)}</a>
            </th>
        )
    }

    const RenderEmpty = (): JSX.Element => {
        return (
            <div className="is-empty">
                <div>
                    <p>
                        <strong>{i18n(emptyMessage)}.</strong>
                    </p>

                    {
                        emptyActionRedirect && 
                        <p>
                            <a href={emptyActionRedirect}>{i18n(emptyActionTitle)}</a>
                        </p>
                    }
                </div>
            </div>
        )
    }

    return (
        <>  
            {
                hideTotal === false && 
                <TotalDataMsg>
                    <span>{i18n('Showing')}</span>
                    <span className="size">{' '} {formatDecimalNumber(state.total) || 0} {' '}</span>
                    <span style={{textTransform: 'lowercase'}}>{i18n(totalDataMessage)}</span>        
                </TotalDataMsg>       
            }
            

            
                <InfiniteScroll
                    dataLength={data.values?.length || 0}
                    next={() => next(data.values?.length + state.totalPage)}
                    hasMore={data.values?.length < state.total}
                    scrollableTarget="scrollableDiv"
                >
                    <ContainerTable id="scrollableDiv">
                        {
                            isLoading ? <div className="loading" style={{zIndex: 1}} />
                            : <>
                                {
                                    data.values?.length > 0 ? (
                                        <>
                                        <StyledTable className="table table-hover">
                                            <thead>
                                                <tr>
                                                    {data.columns.map((column: ColumnProp, index) => (
                                                        <RenderTH key={index} name={column?.name} className={column?.className} />
                                                    ))}
                                                </tr>
                                            </thead>
                
                                            <tbody>
                                                <RenderTR data={data.values} columns={data.columns}/>
                                            </tbody>
                                        
                                        </StyledTable>
                                        {
                                            isPaginating && 
                                            <Paginating>
                                                <div className="loading" />
                                            </Paginating>
                                        }
                                        </>
                                    ) : (
                                        <RenderEmpty />                        
                                    )
                                }
                            </>
                        }
                    
                    </ContainerTable>
                </InfiniteScroll>     
            
              
        </>
    )
}

export default React.memo(CustomTableV2)

const TotalDataMsg  = styled.div`
    color: ${({ theme }) => theme['gray-600']};

    .size{
        font-weight: 600;
    }
`

const Paginating = styled.div`
    position: absolute;
    bottom: -82px;
    background: #fff;
    width: 97%;
    flex: 1;
    text-align: center;
    height: 50px;
`

const StyledTable = styled.table`
    position: relative;
    border-collapse: separate;
    border-spacing: 0;
    width: 99% !important;

    th {
        position: sticky;
        position: -webkit-sticky;
        top: 0;
        z-index: 1;
        background: #fff;
        padding-left: 0 !important;
    }

    tr:hover {
        .table-name {
            color: ${({ theme }) => theme['blue-500']};
        }

        .list-actions {
            display: block;

            ul {
                top: -6px;
                display: flex;
            }
        }
    }
`

const StyledTD = styled.td`
    padding-left: 0 !important;
`

const ContainerTable = styled.div`
    background-color: ${({ theme }) => theme['gray-0']};
    /* height: calc(100vh - 272px); */
    height: calc(100vh - 300px);
    overflow-y: auto;

    &::-webkit-scrollbar {
        width: 6px;
        height: 6px;
    }

    &::-webkit-scrollbar-track {
        border-radius: 10px;
    }

    &::-webkit-scrollbar-thumb {
        background: ${({ theme }) => theme['gray-500']};
        border-radius: 10px;
    }
`
